/* eslint-disable indent */
import { Parameter } from '@src/main/modules/parameters/domain/parameters-management/parameter.interface';
import { toUpdateParameterRequest } from '@src/main/modules/parameters/infrastructure/dto/updateParameterRequest';
import ParameterSectionContainer from '@src/main/modules/parameters/ui/parameters-management/components/ParameterSectionContainer';
import { useParameters } from '@src/main/modules/parameters/ui/parameters-management/hooks/useParameters';
import { useUpdateParameter } from '@src/main/modules/parameters/ui/parameters-management/hooks/useUpdateParameter';
import { isValidHttpUrl } from '@src/main/modules/parameters/ui/parameters-management/utils/isValidHttpUrl';
import { PERMISSIONS } from '@src/main/shared/constants/permissions';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { useToast } from '@src/main/shared/context/ToastContext';
import ContentCard from '@src/main/shared/ui/components/card/ContentCard';
import HeaderTitle from '@src/main/shared/ui/components/header/HeaderTitle';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import { showErrorMessage, showSuccessMessage } from '@src/main/shared/utils/toastUtils';
import { FC, useEffect, useState } from 'react';
import styles from './ParametersManagement.module.scss';

type SectionKey =
  | 'document'
  | 'punctuality'
  | 'pairing'
  | 'alertsCRM'
  | 'connections'
  | 'supportInfo'
  | 'workbreak'
  | 'asuntos'
  | 'byodUpdateUrls';

const sectionOrder: SectionKey[] = [
  'document',
  'punctuality',
  'connections',
  'pairing',
  'alertsCRM',
  'supportInfo',
  'workbreak',
  'asuntos',
  'byodUpdateUrls',
];

const BYOD_UPDATE_URL_PARAMETERS = new Set(['ByodAndroidUpdateUrl', 'ByodIosUpdateUrl']);

const PARAM_TYPE_OVERRIDES: Partial<Record<string, 'text' | 'textarea' | 'switch'>> = {
  RestModuleDisclaimer: 'textarea',
  SupportInformationDetail: 'textarea',
  AlertsCRM: 'switch',
  'personal-tierra': 'text',
  ByodAndroidUpdateUrl: 'text',
  ByodIosUpdateUrl: 'text',
};

const getParamType = (paramName: string): 'number' | 'text' | 'textarea' | 'switch' =>
  PARAM_TYPE_OVERRIDES[paramName] ?? 'number';

const ParametersManagement: FC = () => {
  const { hasPermission } = useAuth();
  const hasEditPermissions = hasPermission(PERMISSIONS.PARAMETERS.MANAGE_PARAMETERS.edit);

  const toast = useToast();
  const { data: parameters, isLoading, refetch } = useParameters();
  const updateParameter = useUpdateParameter();

  const [editMode, setEditMode] = useState<Record<SectionKey, boolean>>({
    document: false,
    punctuality: false,
    pairing: false,
    alertsCRM: false,
    connections: false,
    supportInfo: false,
    workbreak: false,
    asuntos: false,
    byodUpdateUrls: false,
  });

  const [paramsState, setParamsState] = useState<Record<string, Parameter | null>>({});

  const sections: Record<
    SectionKey,
    { title: string; params: Parameter[]; type?: 'text' | 'textarea' | 'switch' }
  > = {
    document: {
      title: 'Alertas documentación',
      params: paramsState.DocumentExpirationDays ? [paramsState.DocumentExpirationDays] : [],
    },
    punctuality: {
      title: 'Puntualidad',
      params:
        paramsState.FlightReadyMinutesShort && paramsState.FlightReadyMinutesLong
          ? [paramsState.FlightReadyMinutesShort, paramsState.FlightReadyMinutesLong]
          : [],
    },
    pairing: {
      title: 'Pairing',
      params:
        paramsState.HistoryReportExpirationHours &&
        paramsState.PILExpirationHours &&
        paramsState.ReportExpirationHours
          ? [
              paramsState.HistoryReportExpirationHours,
              paramsState.PILExpirationHours,
              paramsState.ReportExpirationHours,
            ]
          : [],
    },
    alertsCRM: {
      title: 'Alertas CRM (TCPs)',
      params: paramsState.AlertsCRM ? [paramsState.AlertsCRM] : [],
      type: 'switch',
    },
    connections: {
      title: 'Conexiones',
      params:
        paramsState.CriticalConnectionMinutes && paramsState.LostConnectionMinutes
          ? [paramsState.CriticalConnectionMinutes, paramsState.LostConnectionMinutes]
          : [],
    },
    supportInfo: {
      title: 'Información de soporte',
      params: paramsState.SupportInformationDetail ? [paramsState.SupportInformationDetail] : [],
    },
    workbreak: {
      title: 'Descansos',
      params:
        paramsState.SecondServiceTime &&
        paramsState.OverlapTime &&
        paramsState.TimeBetweenFlights &&
        paramsState.RestModuleDisclaimer
          ? [
              paramsState.SecondServiceTime,
              paramsState.OverlapTime,
              paramsState.TimeBetweenFlights,
              paramsState.RestModuleDisclaimer,
            ]
          : [],
    },
    asuntos: {
      title: 'asuntos',
      params: paramsState['personal-tierra'] ? [paramsState['personal-tierra']] : [],
    },
    byodUpdateUrls: {
      title: 'Enlaces de actualización BYOD',
      params:
        paramsState.ByodAndroidUpdateUrl && paramsState.ByodIosUpdateUrl
          ? [paramsState.ByodAndroidUpdateUrl, paramsState.ByodIosUpdateUrl]
          : [],
      type: 'text',
    },
  };

  const assignParameters = (params: Parameter[]) => {
    const newState: Record<string, Parameter> = {};
    params.forEach((p) => {
      newState[p.parameter] =
        p.parameter === 'AlertsCRM'
          ? { ...p, value: p.value === 'true' || p.value === true }
          : { ...p };
    });
    setParamsState(newState);
  };

  useEffect(() => {
    if (parameters) assignParameters(parameters);
  }, [parameters]);

  const isNumber = (s: string | boolean) => {
    const n = Number(s);
    return !Number.isNaN(n) && n > 0 && Number.isInteger(n);
  };

  const updateParam = async (param: Parameter) => {
    await updateParameter.mutateAsync({
      id: param.parameterId,
      data: toUpdateParameterRequest(param),
    });
  };

  const handleEdit = (key: SectionKey) => setEditMode((prev) => ({ ...prev, [key]: true }));

  const handleRefuse = async (key: SectionKey) => {
    setEditMode((prev) => ({ ...prev, [key]: false }));
    const { data } = await refetch();
    if (data) assignParameters(data);
  };

  const handleAccept = async (key: SectionKey) => {
    try {
      const section = sections[key];
      const invalidParam = section.params.some((p) => {
        const current = paramsState[p.parameter];

        if (!current) return false;

        const paramType = getParamType(p.parameter);

        if (paramType !== 'number') return false;

        return !isNumber(current.value);
      });

      if (invalidParam) {
        showErrorMessage(toast, new Error('Debe introducir un número entero.'));
        return;
      }

      const invalidUrl = section.params.some((parameter) => {
        const current = paramsState[parameter.parameter];
        return (
          current &&
          BYOD_UPDATE_URL_PARAMETERS.has(current.parameter) &&
          !isValidHttpUrl(String(current.value))
        );
      });

      if (invalidUrl) {
        showErrorMessage(toast, new Error('Debe introducir una URL HTTP o HTTPS válida.'));
        return;
      }
      await Promise.all(
        section.params.map((p) => {
          const current = paramsState[p.parameter]!;
          return updateParam({ ...current, value: current.value.toString() });
        }),
      );

      showSuccessMessage(toast, 'Parámetros actualizados correctamente.');
      setEditMode((prev) => ({ ...prev, [key]: false }));

      const { data } = await refetch();
      if (data) assignParameters(data);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      showErrorMessage(toast, err.message || 'Error al actualizar parámetros.');
    }
  };
  return (
    <AppLoading isLoading={isLoading}>
      <ContentCard>
        <HeaderTitle title="Gestión de parámetros" />
        <div className={styles.gridContainer}>
          {sectionOrder.map((sectionKey) => {
            const section = sections[sectionKey];
            if (section.params.length === 0) return null;

            return (
              <ParameterSectionContainer
                key={sectionKey}
                title={section.title}
                parameters={section.params}
                type={section.type}
                editMode={editMode[sectionKey]}
                hasEditPermission={hasEditPermissions}
                onEdit={() => handleEdit(sectionKey)}
                onAccept={() => handleAccept(sectionKey)}
                onRefuse={() => handleRefuse(sectionKey)}
                onChange={(param, value) =>
                  setParamsState((prev) => ({ ...prev, [param.parameter]: { ...param, value } }))
                }
                onRefresh={refetch}
              />
            );
          })}
        </div>
      </ContentCard>
    </AppLoading>
  );
};

export default ParametersManagement;
